import React from "react"
import { useRender } from "@base-ui/react/use-render"
import { Icon } from "../icon"
import type { IconName } from "../icon/assets/icon-map"
import VisuallyHidden from "../visually-hidden"
import { useNavigationContext } from "./navigation-context"
import { matchesPath, resolveActiveIcon } from "./navigation-utils"
import { NavigationButton } from "./navigation-button"

// =============================================================================
// Props
// =============================================================================

export type NavigationItemProps = {
  /**
   * Leading content. Pass an `IconName` string for the common case, or a
   * `React.ReactElement` to render a custom element (e.g. `ProgressCircle`).
   * Shown in both expanded and collapsed modes.
   */
  leading: IconName | React.ReactElement
  label: string
  /**
   * Pathname prefix to match against `Navigation`'s `currentPath`. The item
   * is active when `currentPath.startsWith(pathPrefix)`, so sub-pages (tabs,
   * detail views) under the same prefix are covered automatically.
   */
  pathPrefix?: string
  tooltip?: string
  render?: useRender.RenderProp
  /**
   * Renders after the label. Hidden when the sidebar is collapsed.
   * Use for badges, counters, or other trailing decorations.
   */
  trailing?: React.ReactNode
}

// =============================================================================
// Component
// =============================================================================

/**
 * A flat, non-expandable entry in the sidebar navigation.
 * Must be placed inside `Navigation.Menu` to produce valid `<ul>` / `<li>` markup.
 *
 * Renders a leading icon and a text label. In collapsed mode the label becomes
 * screen-reader-only and a tooltip appears on hover so sighted users can
 * still identify the item.
 *
 * Pass a `render` prop to inject your own link component (e.g. Inertia `Link`)
 * so the DS doesn't need to know about routing.
 *
 * @example
 * <Navigation.Menu>
 *   <Navigation.Item leading="dashboard" label="Dashboard" pathPrefix="/dashboard" render={<Link href="/dashboard" />} />
 *   <Navigation.Item
 *     leading="email"
 *     label="Broadcasts"
 *     pathPrefix="/broadcasts"
 *     render={<Link href="/broadcasts" />}
 *   />
 * </Navigation.Menu>
 */
export function NavigationItem({ leading, label, pathPrefix, tooltip, render, trailing }: NavigationItemProps) {
  const { collapsed, currentPath } = useNavigationContext()
  const isActive = matchesPath(currentPath, pathPrefix)

  return (
    <li data-slot="navigation-item">
      <NavigationButton label={label} isActive={isActive} tooltip={tooltip} render={render}>
        {typeof leading === "string"
          ? <Icon name={isActive ? resolveActiveIcon(leading) : leading} className="shrink-0" aria-hidden />
          : leading
        }
        {collapsed
          ? <VisuallyHidden>{label}</VisuallyHidden>
          : <span className="flex min-w-0 items-center gap-x-2">
              <span className="flex-1 truncate text-left font-semibold">{label}</span>
              {trailing}
            </span>
        }
      </NavigationButton>
    </li>
  )
}

NavigationItem.displayName = "Navigation.Item"
